import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ClipboardDocumentListIcon, MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { useRouting, useRoutingsPage, useRoutingSummary, useSaveRouting, useWorkCenterOptions } from '../../hooks/useRouting';
import { useProductsPage } from '../../hooks/useInventory';
import { RoutingOperationInput } from '../../services/routing.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';

const formatMinutes = (minutes: number) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${formatNumber(value, 2)} min`;
  return `${Math.floor(value / 60)} h ${formatNumber(Math.round(value % 60))} min`;
};

interface StepState {
  key: string;
  sequence: string;
  name: string;
  workCenterId: string;
  setupMinutes: string;
  runMinutesPerUnit: string;
  isOutsourced: boolean;
  description: string;
}

let stepKey = 0;
const newStep = (sequence: number): StepState => ({
  key: `step-${stepKey++}`,
  sequence: String(sequence),
  name: '',
  workCenterId: '',
  setupMinutes: '',
  runMinutesPerUnit: '',
  isOutsourced: false,
  description: '',
});

// Pick a product that has no routing yet
const ProductPicker: React.FC<{ onPick: (product: { id: string; name: string; sku: string }) => void }> = ({ onPick }) => {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const { data, isFetching } = useProductsPage({ page: 1, limit: 8, search: search || undefined });
  const products = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id="routing-product-search"
          type="search"
          autoFocus
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Search the product this routing is for"
          aria-label="Search products"
          className={cn(inputClass, 'pl-9')}
        />
      </div>
      <ul className={cn('divide-y divide-gray-100 rounded-md border border-gray-200', isFetching && 'opacity-60')}>
        {products.length === 0 ? (
          <li className="px-3 py-3 text-sm text-gray-500">{data ? 'No product matches.' : 'Loading…'}</li>
        ) : (
          products.map((product) => (
            <li key={product.id}>
              <button type="button" onClick={() => onPick(product)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-indigo-50">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900">{product.name}</span>
                  <span className="block text-xs text-gray-500">{product.sku}</span>
                </span>
                <span className="shrink-0 text-xs font-semibold text-indigo-600">Choose</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

interface RoutingEditorProps {
  productId: string;
  canEdit: boolean;
  onClose: () => void;
}

const RoutingEditor: React.FC<RoutingEditorProps> = ({ productId, canEdit, onClose }) => {
  const { data: routing, isLoading, isError, error } = useRouting(productId);
  const { data: workCenters = [] } = useWorkCenterOptions();
  const saveRouting = useSaveRouting();

  const [steps, setSteps] = useState<StepState[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // The stored routing fills the form once
  useEffect(() => {
    if (routing && steps === null) {
      setSteps(
        routing.operations.length > 0
          ? routing.operations.map((operation) => ({
              key: `step-${stepKey++}`,
              sequence: String(operation.sequence),
              name: operation.name,
              workCenterId: operation.workCenterId ?? '',
              setupMinutes: operation.setupMinutes ? String(operation.setupMinutes) : '',
              runMinutesPerUnit: operation.runMinutesPerUnit ? String(operation.runMinutesPerUnit) : '',
              isOutsourced: operation.isOutsourced,
              description: operation.description ?? '',
            }))
          : [newStep(10)],
      );
    }
  }, [routing, steps]);

  if (isLoading || steps === null) return <p className="py-10 text-center text-sm text-gray-500">{isError ? getApiErrorMessage(error, 'The routing could not be loaded.') : 'Loading…'}</p>;

  const hadRouting = (routing?.operations.length ?? 0) > 0;
  const setStep = (key: string, field: keyof StepState, value: string | boolean) => {
    setSteps((prev) => (prev ?? []).map((step) => (step.key === key ? { ...step, [field]: value } : step)));
    setErrors((prev) => {
      const { [key]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const addStep = () => setSteps((prev) => [...(prev ?? []), newStep(Math.max(0, ...(prev ?? []).map((step) => Number(step.sequence) || 0)) + 10)]);

  const rateOf = (workCenterId: string) => {
    const workCenter = workCenters.find((candidate) => candidate.id === workCenterId) as { hourlyRate?: number; overheadRate?: number } | undefined;
    return (Number(workCenter?.hourlyRate) || 0) + (Number(workCenter?.overheadRate) || 0);
  };
  const totals = steps.reduce(
    (sum, step) => ({
      setup: sum.setup + (Number(step.setupMinutes) || 0),
      run: sum.run + (Number(step.runMinutesPerUnit) || 0),
      cost: sum.cost + ((Number(step.runMinutesPerUnit) || 0) / 60) * rateOf(step.workCenterId),
    }),
    { setup: 0, run: 0, cost: 0 },
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const seen = new Set<number>();
    const operations: RoutingOperationInput[] = [];
    steps.forEach((step) => {
      const sequence = Number(step.sequence);
      const setup = step.setupMinutes.trim() === '' ? 0 : Number(step.setupMinutes);
      const run = step.runMinutesPerUnit.trim() === '' ? 0 : Number(step.runMinutesPerUnit);
      if (!Number.isInteger(sequence) || sequence < 1 || sequence > 9999) next[step.key] = 'The sequence is a whole number from 1 to 9999';
      else if (seen.has(sequence)) next[step.key] = `Sequence ${sequence} is used twice`;
      else if (step.name.trim().length < 2) next[step.key] = 'Name the operation';
      else if (!Number.isFinite(setup) || setup < 0 || !Number.isFinite(run) || run < 0) next[step.key] = 'Times cannot be negative';
      seen.add(sequence);
      operations.push({
        sequence,
        name: step.name.trim(),
        workCenterId: step.workCenterId || null,
        setupMinutes: Math.round(setup * 100) / 100,
        runMinutesPerUnit: Math.round(run * 10000) / 10000,
        isOutsourced: step.isOutsourced,
        description: step.description.trim() || null,
      });
    });
    if (steps.length === 0 && !hadRouting) next.steps = 'Add at least one operation';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    saveRouting.mutate(
      { productId, operations },
      {
        onSuccess: (saved) => {
          toast.success(saved.operations.length > 0 ? `Routing of ${saved.productName} saved` : `Routing of ${saved.productName} removed`);
          onClose();
        },
        onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The routing could not be saved')),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200">
        <p className="text-sm font-semibold text-gray-900">{routing?.productName}</p>
        <p className="text-xs text-gray-500">{routing?.sku}</p>
      </div>

      <div className="space-y-3">
        {steps.map((step) => (
          <div key={step.key} className={cn('rounded-lg border bg-white p-4', errors[step.key] ? 'border-red-300' : 'border-gray-200')}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-12">
              <Field label="Seq." htmlFor={`${step.key}-seq`} className="lg:col-span-1">
                <input id={`${step.key}-seq`} type="number" min="1" step="1" disabled={!canEdit} value={step.sequence} onChange={(event) => setStep(step.key, 'sequence', event.target.value)} className={cn(inputClass, 'tabular-nums')} />
              </Field>
              <Field label="Operation" htmlFor={`${step.key}-name`} required className="col-span-2 lg:col-span-4">
                <input
                  id={`${step.key}-name`}
                  type="text"
                  maxLength={100}
                  disabled={!canEdit}
                  value={step.name}
                  onChange={(event) => setStep(step.key, 'name', event.target.value)}
                  aria-label="Operation name"
                  className={cn(inputClass, errors[step.key] && step.name.trim().length < 2 && inputErrorClass)}
                  placeholder="For example CNC turning"
                />
              </Field>
              <Field label="Work center" htmlFor={`${step.key}-wc`} className="col-span-2 lg:col-span-3">
                <select id={`${step.key}-wc`} disabled={!canEdit} value={step.workCenterId} onChange={(event) => setStep(step.key, 'workCenterId', event.target.value)} className={inputClass}>
                  <option value="">None</option>
                  {workCenters.map((workCenter) => (
                    <option key={workCenter.id} value={workCenter.id}>
                      {workCenter.name} ({workCenter.code})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Setup (min)" htmlFor={`${step.key}-setup`} className="lg:col-span-2">
                <input id={`${step.key}-setup`} type="number" min="0" step="0.01" disabled={!canEdit} value={step.setupMinutes} onChange={(event) => setStep(step.key, 'setupMinutes', event.target.value)} className={cn(inputClass, 'text-right tabular-nums')} placeholder="0" />
              </Field>
              <Field label="Run (min / unit)" htmlFor={`${step.key}-run`} className="lg:col-span-2">
                <input id={`${step.key}-run`} type="number" min="0" step="0.0001" disabled={!canEdit} value={step.runMinutesPerUnit} onChange={(event) => setStep(step.key, 'runMinutesPerUnit', event.target.value)} className={cn(inputClass, 'text-right tabular-nums')} placeholder="0" />
              </Field>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" disabled={!canEdit} checked={step.isOutsourced} onChange={(event) => setStep(step.key, 'isOutsourced', event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                Done outside by a job worker
              </label>
              {canEdit && (
                <button type="button" onClick={() => setSteps((prev) => (prev ?? []).filter((candidate) => candidate.key !== step.key))} className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600" aria-label={`Remove operation ${step.name || step.sequence}`}>
                  <XMarkIcon className="mr-1 h-3.5 w-3.5" />
                  Remove
                </button>
              )}
            </div>
            {errors[step.key] && <p className="mt-2 text-sm text-red-600">{errors[step.key]}</p>}
          </div>
        ))}
        {errors.steps && <p className="text-sm text-red-600">{errors.steps}</p>}
        {steps.length === 0 && hadRouting && (
          <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-amber-600/20">
            Saving now removes the routing. Work orders already made keep their operations.
          </p>
        )}
        {canEdit && (
          <button type="button" onClick={addStep} className={secondaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Operation
          </button>
        )}
      </div>

      <dl className="grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-4 text-sm ring-1 ring-gray-200">
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Setup per work order</dt>
          <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMinutes(totals.setup)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Run time per unit</dt>
          <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMinutes(totals.run)}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Run cost per unit</dt>
          <dd className="mt-1 font-semibold text-gray-900 tabular-nums">{formatMoney(totals.cost)}</dd>
        </div>
      </dl>

      <FormActions>
        <button type="button" onClick={onClose} disabled={saveRouting.isPending} className={secondaryButtonClass}>
          {canEdit ? 'Cancel' : 'Close'}
        </button>
        {canEdit && (
          <button type="submit" disabled={saveRouting.isPending} className={primaryButtonClass}>
            {saveRouting.isPending ? 'Saving…' : 'Save Routing'}
          </button>
        )}
      </FormActions>
    </form>
  );
};

const Routings: React.FC = () => {
  const { can } = usePermissions();
  const canEdit = can('manufacturing.update');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [picking, setPicking] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useRoutingsPage({ page, limit, search: search || undefined });
  const { data: summary } = useRoutingSummary();

  const routings = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const stats = [
    { label: 'Products with a routing', value: summary ? formatNumber(summary.productsWithRouting) : '—', hint: `of ${summary ? formatNumber(summary.activeProducts) : '—'} active products` },
    { label: 'Operations', value: summary ? formatNumber(summary.operations) : '—', hint: 'Across all routings' },
    { label: 'Without a work center', value: summary ? formatNumber(summary.operationsWithoutWorkCenter) : '—', hint: 'These carry no cost' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Routings</h1>
          <p className="mt-1 text-sm text-gray-500">
            How each product is made, operation by operation. A new work order takes the routing of its product; time is then booked on job cards.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/job-cards" className={secondaryButtonClass}>
            <ClipboardDocumentListIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Job Cards
          </Link>
          {canEdit && (
            <button type="button" onClick={() => setPicking(true)} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              New Routing
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by product, SKU or operation" aria-label="Search routings" className={cn(inputClass, 'pl-9')} />
          </div>
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The routings could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Product</th>
                  <th scope="col" className={thClass}>Operations</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Setup</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Run / unit</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Run cost / unit</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 6 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : routings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{search ? 'No routing matches' : 'No routings yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">{search ? 'Try a different search.' : 'Describe how a product is made to track its operations on work orders.'}</p>
                    </td>
                  </tr>
                ) : (
                  routings.map((routing) => (
                    <tr key={routing.productId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => setEditingProductId(routing.productId)} className="text-left text-sm font-medium text-gray-900 hover:text-indigo-600">
                          {routing.productName}
                        </button>
                        <p className="mt-0.5 text-xs text-gray-500">{routing.sku}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{routing.steps}</p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {routing.operations} step(s)
                          {routing.outsourced > 0 && <span className={cn(badgeClass, 'ml-2 bg-amber-50 text-amber-800 ring-amber-600/20')}>{routing.outsourced} outside</span>}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMinutes(routing.setupMinutes)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatMinutes(routing.runMinutesPerUnit)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(routing.runCostPerUnit)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setEditingProductId(routing.productId)} title={canEdit ? 'Edit' : 'View'} aria-label={`Open the routing of ${routing.productName}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      {/* One dialog for both steps: closing a first dialog would pull the focus away from the form */}
      <Modal
        isOpen={picking || !!editingProductId}
        onClose={() => {
          setPicking(false);
          setEditingProductId(null);
        }}
        title={editingProductId ? 'Routing' : 'New Routing'}
        description={
          editingProductId
            ? 'Operations in the order they are done. Work orders already made keep the routing they were made with.'
            : 'Choose the product. If it already has a routing, that routing opens.'
        }
        size="2xl"
      >
        {editingProductId ? (
          <RoutingEditor key={editingProductId} productId={editingProductId} canEdit={canEdit} onClose={() => setEditingProductId(null)} />
        ) : picking ? (
          <ProductPicker
            onPick={(product) => {
              setPicking(false);
              setEditingProductId(product.id);
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default Routings;
